<form [formGroup]="form" class="form-section">
  <app-form-control [columnLayout]="true" size="large">
    <app-form-label [required]="true"> Name</app-form-label>
    <input
      data-dd-action-name="edit-asset-details.name-input"
      type="text"
      class="form-control"
      placeholder="Asset name&hellip;"
      [class.is-invalid]="
        form.controls.name.touched && form.controls.name.invalid
      "
      formControlName="name"
    />
    @if (form.controls.name.hasError('required')) {
      <div class="invalid-feedback">Name is required</div>
    }
    @if (form.controls.name.getError('maxlength'); as error) {
      <div class="invalid-feedback">
        Name must be less than {{ error.requiredLength }} characters
      </div>
    }
  </app-form-control>

  <app-form-control [columnLayout]="true" size="medium">
    <app-form-label>
      Code / Number
      @if (isAutoCodeOn) {
        <i-lucide
          name="info"
          ngbTooltip="When this is blank a code will be automatically generated."
        ></i-lucide>
      }
    </app-form-label>
    <input
      data-dd-action-name="edit-asset-details.code-input"
      type="text"
      class="form-control"
      [placeholder]="
        isAutoCodeOn ? ' - Auto -' : 'Unique code or number&hellip;'
      "
      [class.is-invalid]="
        form.controls.code.touched && form.controls.code.invalid
      "
      formControlName="code"
    />
    @if (form.controls.code.getError('maxlength'); as error) {
      <div class="invalid-feedback">
        Code / Number must be less than {{ error.requiredLength }} characters
      </div>
    }
  </app-form-control>

  <app-form-control [columnLayout]="true" size="large">
    <app-form-label> Description </app-form-label>
    <textarea
      data-dd-action-name="edit-asset-details.description-input"
      class="form-control"
      placeholder="Description of the asset&hellip;"
      [class.is-invalid]="
        form.controls.description.touched && form.controls.description.invalid
      "
      formControlName="description"
    ></textarea>
    @if (form.controls.description.getError('maxlength'); as error) {
      <div class="invalid-feedback">
        Description must be less than {{ error.requiredLength }} characters
      </div>
    }
  </app-form-control>

  @if (isNew()) {
    <app-form-control [columnLayout]="true" margin="small" size="medium">
      <app-form-label [required]="true"> Group </app-form-label>
      <app-asset-group-selector
        formControlName="groupId"
        #GroupSelector
        [class.is-invalid]="
          form.controls.groupId.touched && form.controls.groupId.invalid
        "
        [canAddNewGroup]="true"
        (newAssetGroup)="newAssetGroup = $event"
      >
      </app-asset-group-selector>
      @if (form.controls.groupId.hasError('required')) {
        <div class="invalid-feedback">Group is required</div>
      }
    </app-form-control>
    <app-form-control [columnLayout]="true" size="small">
      <app-form-label [required]="true">Cost </app-form-label>
      <app-currency-input formControlName="purchasePrice">
        @if (form.controls.purchasePrice.hasError('required')) {
          <div class="invalid-feedback">Cost is required</div>
        }
      </app-currency-input>
    </app-form-control>
    <app-form-control [columnLayout]="true" margin="small" size="small">
      <app-form-label [required]="true">Acquisition Date </app-form-label>
      <app-date-input
        appValidationContext
        formControlName="acquisitionDate"
        [minDate]="parentAsset?.acquisitionDate"
      >
        @if (form.controls.acquisitionDate.hasError('required')) {
          <div class="invalid-feedback">Acquisition Date is required</div>
        }
      </app-date-input>
    </app-form-control>
    <app-form-control [columnLayout]="true" size="small">
      <app-form-label> First Use Date </app-form-label>
      <ng-template #firstUseFooter>
        <div class="p-1 text-center bg-light">
          Must be on or after acquisition
        </div>
      </ng-template>
      <app-date-input
        appValidationContext
        formControlName="firstUseDate"
        [minDate]="form.controls.acquisitionDate.value"
        [maxDate]="minReassessmentDate"
        [footerTemplate]="firstUseFooter"
      >
        @if (form.controls.firstUseDate.getError('minDate'); as error) {
          <div class="invalid-feedback">
            Must be on or after Acquisition Date ({{ error.minDate | date }})
          </div>
        }
        @if (form.controls.firstUseDate.getError('maxDate'); as error) {
          <div class="invalid-feedback">
            Must be on or before first reassessment ({{ error.maxDate | date }})
          </div>
        }
      </app-date-input>
    </app-form-control>
    <app-form-control [columnLayout]="true" size="medium">
      <app-form-label> Quantity </app-form-label>
      <div class="input-group mb-6">
        <input
          data-dd-action-name="edit-asset-details.quantity-input"
          type="number"
          class="form-control text-end"
          [class.is-invalid]="
            form.controls.quantity.touched && form.controls.quantity.invalid
          "
          formControlName="quantity"
          [authorizeFeature]="Feature.Quantities"
          authorizeEnable
        />
        <select
          data-dd-action-name="edit-asset-details.quantity-units-select"
          class="form-select"
          [class.is-invalid]="
            form.controls.quantityUnits.touched &&
            form.controls.quantityUnits.invalid
          "
          formControlName="quantityUnits"
          [authorizeFeature]="Feature.Quantities"
          authorizeEnable
        >
          <option value="">&ndash;</option>
          @for (group of QUANTITY_UNITS; track group) {
            <optgroup [label]="group.label">
              @for (unit of group.units; track unit) {
                <option [value]="unit.value">
                  {{ unit.name }} ({{ unit.value }})
                </option>
              }
            </optgroup>
          }
        </select>
        @if (form.controls.quantity.hasError('min')) {
          <div class="invalid-feedback">Quantity must be more than zero</div>
        }
      </div>
    </app-form-control>
    @if (listFieldsResponse) {
      <app-new-asset-fields-form
        class="large"
        [listFieldsResponse]="listFieldsResponse"
        [fieldControls]="form.controls.fields"
        [classificationDefaults]="classificationDefaults"
      ></app-new-asset-fields-form>
    }
    <app-form-control [columnLayout]="true" size="large">
      <app-form-label>Custom Fields</app-form-label>
      <app-custom-fields-edit
        class="large"
        [objectType]="RegisterObjectType.Asset"
        [form]="form.controls.tags"
      ></app-custom-fields-edit>
    </app-form-control>
  }
</form>
